import React, { useState, useEffect } from 'react';
import { Edit, FileText, ToggleLeft, ToggleRight, Plus, Search, Download, Eye, Trash2 } from 'lucide-react';
import axios from '../api/axios';
import toast from 'react-hot-toast';
import VendorModal from './vendors/VendorModal';
import VendorDetailsModal from './vendors/VendorDetailsModal';
import { usePermissions } from '../hooks/usePermissions';

const Vendors = () => {
  const { hasPermission } = usePermissions();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [viewingVendor, setViewingVendor] = useState(null);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, thisMonth: 0 });
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    status: '',
    category: '',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState({});

  const vendorTypes = [
    { value: 'SPARE_PARTS', label: 'Spare Parts' },
    { value: 'CONSUMABLES', label: 'Consumables' },
    { value: 'TOOLS', label: 'Tools' },
    { value: 'SERVICES', label: 'Services' },
    { value: 'SERVICE_PROVIDER', label: 'Service Provider' },
    { value: 'EQUIPMENT', label: 'Equipment' },
    { value: 'OTHER', label: 'Other' }
  ];

  const vendorStatuses = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' }
  ];

  useEffect(() => {
    fetchVendors();
    fetchStats();
  }, [filters.search, filters.type, filters.status, filters.category, filters.page, filters.limit]);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });

      const response = await axios.get(`/api/vendors?${params}`);
      const vendorsData = (response.data?.vendors || []).map(v => {
        let tags = [];
        try {
          tags = typeof v?.tags === 'string' ? JSON.parse(v.tags) : (Array.isArray(v?.tags) ? v.tags : []);
        } catch (e) {
          tags = [];
        }
        return { ...v, tags: tags || [], _count: v?._count || { purchaseOrders: 0, inventory: 0 } };
      });
      setVendors(vendorsData || []);
      setPagination(response.data?.pagination || {});
    } catch (error) {
      setVendors([]);
      toast.error('Failed to fetch vendors');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/vendors/stats');
      setStats(response.data);
    } catch (error) {
    }
  };

  const handleEdit = (vendor) => {
    setEditingVendor(vendor);
    setShowModal(true);
  };

  const handleView = (vendor) => {
    setViewingVendor(vendor);
    setShowDetailsModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this vendor?')) {
      try {
        await axios.delete(`/api/vendors/${id}`);
        toast.success('Vendor deleted successfully');
        fetchVendors();
        fetchStats();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to delete vendor');
      }
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    try {
      await axios.patch(`/api/vendors/${id}/status`, { status: newStatus });
      toast.success(`Vendor ${newStatus.toLowerCase()} successfully`);
      fetchVendors();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update vendor status');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingVendor(null);
    fetchVendors();
    fetchStats();
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key] && key !== 'page' && key !== 'limit') {
          params.append(key, filters[key]);
        }
      });
      
      const response = await axios.get(`/api/vendors/export?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'vendors.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Vendors exported successfully');
    } catch (error) {
      toast.error('Failed to export vendors');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-800';
      case 'INACTIVE': return 'bg-gray-100 text-gray-800';
      case 'SUSPENDED': return 'bg-red-100 text-red-800';
      case 'PENDING_APPROVAL': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'SPARE_PARTS': return 'bg-blue-100 text-blue-800';
      case 'CONSUMABLES': return 'bg-purple-100 text-purple-800';
      case 'SERVICE_PROVIDER': return 'bg-green-100 text-green-800';
      case 'EQUIPMENT': return 'bg-orange-100 text-orange-800';
      case 'TOOLS': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Vendors Management</h1>
        {hasPermission('vendors.create') && (
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Vendor
        </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Vendors</p>
              <p className="text-3xl font-bold text-blue-600 mt-2">{stats?.total || 0}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Vendors</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{stats?.active || 0}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <ToggleRight className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Inactive Vendors</p>
              <p className="text-3xl font-bold text-red-600 mt-2">{stats?.inactive || 0}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <ToggleLeft className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">This Month</p>
              <p className="text-3xl font-bold text-purple-600 mt-2">{stats?.thisMonth || 0}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Plus className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search vendors..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <select
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value, page: 1 })}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Types</option>
            {vendorTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>

          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            {vendorStatuses.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Category..."
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value, page: 1 })}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          <button 
            onClick={handleExport}
            className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendor Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Orders
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={`loading-${index}`}>
                    <td colSpan="6" className="px-6 py-4">
                      <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                    </td>
                  </tr>
                ))
              ) : Array.isArray(vendors) && vendors.length > 0 ? (
                vendors.map((vendor, index) => (
                  <tr key={vendor?.id || vendor?._id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{vendor?.name || 'Unnamed Vendor'}</div>
                        <div className="text-sm text-gray-500">{vendor?.contactPerson || 'No contact person'}</div>
                        {vendor?.category && (
                          <div className="text-xs text-gray-400">{vendor.category}</div>
                        )}
                        {Array.isArray(vendor?.tags) && vendor.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {vendor.tags.slice(0, 2).map((tag, idx) => (
                              <span key={idx} className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                                {tag}
                              </span>
                            ))}
                            {vendor.tags.length > 2 && (
                              <span className="text-xs text-gray-400">+{vendor.tags.length - 2}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(vendor?.type || '')}`}>
                        {vendor?.type?.replace('_', ' ') || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        <div>{vendor?.email || 'No email'}</div>
                        <div className="text-gray-500">{vendor?.phone || 'No phone'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(vendor?.status || '')}`}>
                        {vendor?.status?.replace('_', ' ') || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        <div>Orders: {vendor?._count?.purchaseOrders || 0}</div>
                        <div className="text-gray-500">Parts: {vendor?._count?.inventory || 0}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleView(vendor)}
                          className="text-blue-600 hover:text-blue-800"
                          title="View Details"
                        >
                          👁️
                        </button>
                        {hasPermission('vendors.update') && (
                        <button
                          onClick={() => handleEdit(vendor)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        )}
                        {hasPermission('vendors.update') && (
                        <button
                          onClick={() => handleToggleStatus(vendor?.id, vendor?.status)}
                          className={vendor?.status === 'ACTIVE' ? "text-red-600 hover:text-red-800" : "text-green-600 hover:text-green-800"}
                          title={vendor?.status === 'ACTIVE' ? "Deactivate" : "Activate"}
                        >
                          {vendor?.status === 'ACTIVE' ? '⏸️' : '▶️'}
                        </button>
                        )}
                        {hasPermission('vendors.delete') && (
                        <button
                          onClick={() => handleDelete(vendor?.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          🗑️
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    No vendors found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {pagination?.pages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination?.currentPage - 1) * pagination?.limit) + 1} to{' '}
              {Math.min(pagination?.currentPage * pagination?.limit, pagination?.total)} of{' '}
              {pagination?.total} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={pagination?.currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={pagination?.currentPage === pagination?.pages}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <VendorModal
          vendor={editingVendor}
          vendorTypes={vendorTypes}
          vendorStatuses={vendorStatuses}
          onClose={handleModalClose}
        />
      )}

      {showDetailsModal && viewingVendor && (
        <VendorDetailsModal
          vendor={viewingVendor}
          onClose={() => setShowDetailsModal(false)}
        />
      )}
    </div>
  );
};

export default Vendors;
